import React, { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  BarChart3, Users, TrendingUp, TrendingDown, Minus, Loader2, AlertCircle,
  ArrowRight, Globe, Layers, Inbox,
} from 'lucide-react';
import {
  ResponsiveContainer, BarChart, Bar, XAxis, YAxis, Tooltip, CartesianGrid,
} from 'recharts';
import { adminApi } from './AdminAuthContext';

/**
 * Admin landing dashboard — KPI cards + 30-day lead trend + breakdowns.
 *
 * Pulls a single `/admin/leads/stats?days=N` aggregate so the page renders
 * fast even with thousands of leads (no per-lead network round-trips).
 */

const RANGES = [
  { id: 7,   label: '7 Tage' },
  { id: 30,  label: '30 Tage' },
  { id: 90,  label: '90 Tage' },
  { id: 365, label: '12 Monate' },
];

const STATUS_META = {
  new:        { label: 'Neu',        color: '#F26B1A' },
  contacted:  { label: 'Kontaktiert', color: '#E0A930' },
  closed:     { label: 'Gewonnen',   color: '#7FC242' },
  lost:       { label: 'Verloren',   color: '#C03A1A' },
};

const SOURCE_LABEL = {
  configurator:  'Konfigurator',
  landing_page:  'Landingpage',
  category:      'Kategorie-Seite',
  product:       'Produktseite',
  exit_intent:   'Exit-Intent',
  unknown:       'Unbekannt',
};

function fmtPct(n) {
  if (!Number.isFinite(n)) return '—';
  const sign = n > 0 ? '+' : '';
  return `${sign}${n.toFixed(0)}%`;
}

function dateLabel(iso) {
  // 'YYYY-MM-DD' → 'DD.MM.'
  if (!iso || iso.length < 10) return iso || '';
  const [, m, d] = iso.split('-');
  return `${d}.${m}.`;
}

function DeltaBadge({ current, prev }) {
  if (prev === 0 && current === 0) {
    return (
      <span data-testid="kpi-delta" className="inline-flex items-center gap-1 text-[11px] text-[#5C5852]">
        <Minus className="w-3 h-3" /> keine Vorperiode
      </span>
    );
  }
  if (prev === 0) {
    return (
      <span data-testid="kpi-delta" className="inline-flex items-center gap-1 text-[11px] font-semibold text-[#7FC242]">
        <TrendingUp className="w-3 h-3" /> neu
      </span>
    );
  }
  const delta = ((current - prev) / prev) * 100;
  const Icon = delta > 0 ? TrendingUp : delta < 0 ? TrendingDown : Minus;
  const color = delta > 0 ? '#7FC242' : delta < 0 ? '#C03A1A' : '#5C5852';
  return (
    <span data-testid="kpi-delta" className="inline-flex items-center gap-1 text-[11px] font-semibold" style={{ color }}>
      <Icon className="w-3 h-3" /> {fmtPct(delta)}
    </span>
  );
}

function KpiCard({ testId, icon: Icon, label, value, hint, accent = '#F26B1A' }) {
  return (
    <article data-testid={testId} className="bg-white rounded-2xl border border-[#E5E0D2] p-4 min-h-[118px]">
      <div className="flex items-center gap-2 text-[11.5px] uppercase tracking-[0.14em] font-semibold text-[#5C5852]">
        <span className="inline-flex w-7 h-7 rounded-full items-center justify-center"
              style={{ backgroundColor: `${accent}1A`, color: accent }}>
          <Icon className="w-3.5 h-3.5" />
        </span>
        {label}
      </div>
      <p className="mt-2 font-display text-[26px] sm:text-[30px] leading-none text-[#1F1D1A] tabular-nums">{value}</p>
      {hint && <div className="mt-1.5">{hint}</div>}
    </article>
  );
}

function BreakdownList({ testId, title, icon: Icon, items, total, labelFor, colorFor }) {
  if (!items.length) {
    return (
      <section data-testid={testId} className="bg-white rounded-2xl border border-[#E5E0D2] p-4 h-full min-h-0 flex flex-col">
        <header className="flex items-center gap-2 pb-2 mb-2 border-b border-[#E5E0D2] shrink-0">
          <Icon className="w-4 h-4 text-[#F26B1A]" />
          <p className="font-display text-[16px] text-[#1F1D1A]">{title}</p>
        </header>
        <p className="text-[12.5px] text-[#5C5852]">Noch keine Daten.</p>
      </section>
    );
  }
  return (
    <section data-testid={testId} className="bg-white rounded-2xl border border-[#E5E0D2] p-4 h-full min-h-0 flex flex-col">
      <header className="flex items-center gap-2 pb-2 mb-2 border-b border-[#E5E0D2] shrink-0">
        <Icon className="w-4 h-4 text-[#F26B1A]" />
        <p className="font-display text-[16px] text-[#1F1D1A]">{title}</p>
      </header>
      <ul className="space-y-2 overflow-y-auto pr-1 min-h-0">
        {items.map((it) => {
          const pct = total > 0 ? (it.count / total) * 100 : 0;
          const color = colorFor?.(it.key) ?? '#F26B1A';
          return (
            <li key={it.key} data-testid={`${testId}-row-${it.key}`} className="space-y-1">
              <div className="flex items-center justify-between gap-2 text-[12.5px]">
                <span className="text-[#1F1D1A] truncate">{labelFor?.(it.key) ?? it.key}</span>
                <span className="tabular-nums text-[#5C5852]">{it.count} <span className="text-[11px]">({pct.toFixed(0)}%)</span></span>
              </div>
              <div className="h-1.5 bg-[#FAF8F2] rounded-full overflow-hidden">
                <div className="h-full rounded-full" style={{ width: `${pct}%`, backgroundColor: color }} />
              </div>
            </li>
          );
        })}
      </ul>
    </section>
  );
}

function ChartTooltip({ active, payload, label }) {
  if (!active || !payload?.length) return null;
  const v = payload[0].value;
  return (
    <div className="bg-[#1F1D1A] text-white px-3 py-2 rounded-lg text-[12px] shadow-lg">
      <p className="opacity-70 tabular-nums">{label}</p>
      <p className="font-semibold tabular-nums">{v} Lead{v === 1 ? '' : 's'}</p>
    </div>
  );
}

function TopProductsPanel({ items = [] }) {
  return (
    <section data-testid="dash-top-products" className="bg-white rounded-2xl border border-[#E5E0D2] p-4 h-full min-h-0 flex flex-col">
      <header className="flex items-center gap-2 pb-2 mb-2 border-b border-[#E5E0D2] shrink-0">
        <BarChart3 className="w-4 h-4 text-[#F26B1A]" />
        <p className="font-display text-[16px] text-[#1F1D1A]">Top-Produkte</p>
      </header>
      {items.length ? (
        <ul className="space-y-2 overflow-y-auto pr-1 min-h-0">
          {items.map((p) => {
            const max = items[0].count || 1;
            const pct = (p.count / max) * 100;
            return (
              <li key={p.product} data-testid={`top-product-${p.product}`} className="space-y-1">
                <div className="flex items-center justify-between gap-2 text-[12.5px]">
                  <span className="text-[#1F1D1A] truncate">{p.product}</span>
                  <span className="tabular-nums text-[#5C5852]">{p.count}</span>
                </div>
                <div className="h-1.5 bg-[#FAF8F2] rounded-full overflow-hidden">
                  <div className="h-full rounded-full bg-[#F26B1A]" style={{ width: `${pct}%` }} />
                </div>
              </li>
            );
          })}
        </ul>
      ) : (
        <p className="text-[12.5px] text-[#5C5852]">Noch keine Produkt-Anfragen erfasst.</p>
      )}
    </section>
  );
}

export default function AdminDashboardPage() {
  const [days, setDays] = useState(30);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    let cancelled = false;
    setLoading(true); setError(null);
    adminApi.get('/admin/leads/stats', { params: { days } })
      .then(({ data }) => { if (!cancelled) setStats(data); })
      .catch((e) => { if (!cancelled) setError(e?.response?.data?.detail || 'Statistik konnte nicht geladen werden.'); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [days]);

  const chartData = useMemo(() => (stats?.daily || []).map((d) => ({
    date: dateLabel(d.date), count: d.count, iso: d.date,
  })), [stats]);

  const statusItems = useMemo(() => {
    const obj = stats?.by_status || {};
    return Object.entries(obj)
      .map(([k, n]) => ({ key: k, count: n }))
      .sort((a, b) => b.count - a.count);
  }, [stats]);

  const sourceItems = useMemo(() => {
    const obj = stats?.by_source || {};
    return Object.entries(obj)
      .map(([k, n]) => ({ key: k, count: n }))
      .sort((a, b) => b.count - a.count);
  }, [stats]);

  const langItems = useMemo(() => {
    const obj = stats?.by_language || {};
    return Object.entries(obj)
      .map(([k, n]) => ({ key: k, count: n }))
      .sort((a, b) => b.count - a.count);
  }, [stats]);

  return (
    <div data-testid="admin-dashboard-page" className="w-full max-w-none md:h-full md:min-h-0 md:overflow-hidden flex flex-col gap-4">
      <div className="flex items-baseline justify-between gap-3 flex-wrap shrink-0">
        <div className="flex items-center gap-3">
          <span className="inline-flex w-9 h-9 sm:w-10 sm:h-10 rounded-full bg-[#F26B1A]/10 text-[#F26B1A] items-center justify-center">
            <BarChart3 className="w-4 h-4 sm:w-5 sm:h-5" />
          </span>
          <h1 className="font-display text-[22px] sm:text-[24px] lg:text-[28px] text-[#1F1D1A] leading-none">Dashboard</h1>
        </div>
        <div data-testid="dashboard-range-picker" className="bg-white rounded-xl border border-[#E5E0D2] p-1 inline-flex gap-1 flex-wrap max-w-full overflow-x-auto">
          {RANGES.map((r) => (
            <button key={r.id} type="button" onClick={() => setDays(r.id)}
              data-testid={`range-${r.id}`}
              className={`px-3 py-1.5 rounded-lg text-[12px] font-semibold transition-colors ${
                days === r.id ? 'bg-[#F26B1A] text-white' : 'text-[#5C5852] hover:bg-[#FAF8F2]'
              }`}>
              {r.label}
            </button>
          ))}
        </div>
      </div>

      {error && (
        <p data-testid="dashboard-err" className="inline-flex items-start gap-2 text-[12.5px] text-[#C03A1A]">
          <AlertCircle className="w-4 h-4 mt-0.5" /> {error}
        </p>
      )}

      {loading && !stats ? (
        <div className="p-10 text-center text-[#5C5852]">
          <Loader2 className="inline w-5 h-5 animate-spin" />
        </div>
      ) : stats && (
        <div
          className="flex-1 min-h-0 flex flex-col md:grid gap-4"
          style={{ gridTemplateRows: 'auto minmax(190px, 1fr) minmax(140px, 0.5fr)' }}
        >
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 2xl:grid-cols-5 gap-3 shrink-0">
            <KpiCard
              testId="kpi-period-total"
              icon={Inbox}
              label={`Leads · ${days}T`}
              value={stats.period_total}
              hint={<DeltaBadge current={stats.period_total} prev={stats.period_prev_total} />}
            />
            <KpiCard
              testId="kpi-total"
              icon={Users}
              label="Gesamt"
              value={stats.total}
              accent="#7FC242"
            />
            <KpiCard
              testId="kpi-new"
              icon={Layers}
              label="Offen (Neu)"
              value={stats.by_status?.new || 0}
              accent="#F26B1A"
            />
            <KpiCard
              testId="kpi-won"
              icon={TrendingUp}
              label="Gewonnen"
              value={stats.by_status?.closed || 0}
              accent="#7FC242"
              hint={
                stats.total > 0 ? (
                  <span className="text-[11px] text-[#5C5852]">
                    Conversion: <span className="font-semibold text-[#1F1D1A] tabular-nums">
                      {(((stats.by_status?.closed || 0) / stats.total) * 100).toFixed(1)}%
                    </span>
                  </span>
                ) : null
              }
            />
            <section className="hidden 2xl:block bg-white rounded-2xl border border-[#E5E0D2] p-4 min-h-[118px]">
              <header className="flex items-center gap-2 pb-2 mb-2 border-b border-[#E5E0D2]">
                <ArrowRight className="w-4 h-4 text-[#F26B1A]" />
                <p className="font-display text-[16px] text-[#1F1D1A]">Schnellzugriff</p>
              </header>
              <div className="space-y-1.5">
                <Link to="/admin/leads" className="block px-3 py-1.5 rounded-lg bg-[#FAF8F2] text-[12.5px] font-semibold text-[#1F1D1A] hover:text-[#F26B1A]">Leads bearbeiten</Link>
                <Link to="/admin/content" className="block px-3 py-1.5 rounded-lg bg-[#FAF8F2] text-[12.5px] font-semibold text-[#1F1D1A] hover:text-[#F26B1A]">Inhalte pflegen</Link>
                <Link to="/admin/media" className="block px-3 py-1.5 rounded-lg bg-[#FAF8F2] text-[12.5px] font-semibold text-[#1F1D1A] hover:text-[#F26B1A]">Medienpool oeffnen</Link>
              </div>
            </section>
          </div>

          <section data-testid="dashboard-trend" className="bg-white rounded-2xl border border-[#E5E0D2] p-4 min-h-0 flex flex-col">
            <header className="flex items-baseline justify-between gap-3 mb-3 flex-wrap shrink-0">
              <div>
                <p className="font-display text-[18px] text-[#1F1D1A]">Lead-Verlauf</p>
                <p className="text-[11.5px] text-[#5C5852] mt-0.5">
                  Tageswerte über die letzten {days} Tage.
                </p>
              </div>
              <Link to="/admin/leads" data-testid="dashboard-leads-link"
                className="inline-flex items-center gap-1 text-[12px] font-semibold text-[#F26B1A] hover:text-[#D85A0A]">
                Alle Leads ansehen <ArrowRight className="w-3.5 h-3.5" />
              </Link>
            </header>
            <div className="flex-1 min-h-[150px]" style={{ width: '100%' }}>
              <ResponsiveContainer>
                <BarChart data={chartData} margin={{ top: 6, right: 8, left: -16, bottom: 0 }}>
                  <CartesianGrid stroke="#E5E0D2" strokeDasharray="2 4" vertical={false} />
                  <XAxis dataKey="date" stroke="#5C5852" fontSize={11}
                    tick={{ fill: '#5C5852' }} tickLine={false} axisLine={{ stroke: '#E5E0D2' }}
                    interval="preserveStartEnd" minTickGap={24} />
                  <YAxis stroke="#5C5852" fontSize={11} allowDecimals={false}
                    tick={{ fill: '#5C5852' }} tickLine={false} axisLine={false} width={36} />
                  <Tooltip content={<ChartTooltip />} cursor={{ fill: '#F26B1A0F' }} />
                  <Bar dataKey="count" fill="#F26B1A" radius={[4, 4, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </section>

          <div className="grid grid-cols-1 lg:grid-cols-2 2xl:grid-cols-4 gap-4 md:min-h-0 md:overflow-hidden">
            <BreakdownList
              testId="dash-status"
              title="Nach Status"
              icon={Layers}
              items={statusItems}
              total={stats.total}
              labelFor={(k) => STATUS_META[k]?.label || k}
              colorFor={(k) => STATUS_META[k]?.color || '#F26B1A'}
            />
            <BreakdownList
              testId="dash-source"
              title="Nach Quelle"
              icon={Inbox}
              items={sourceItems}
              total={stats.total}
              labelFor={(k) => SOURCE_LABEL[k] || k}
            />
            <BreakdownList
              testId="dash-lang"
              title="Nach Sprache"
              icon={Globe}
              items={langItems}
              total={stats.total}
              labelFor={(k) => k === 'nl' ? 'Niederländisch' : k === 'de' ? 'Deutsch' : k}
            />
            <TopProductsPanel items={stats.top_products || []} />
          </div>
        </div>
      )}
    </div>
  );
}
